import React, { useState, useEffect, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';

import IModel from 'models/model';
import InputTextField, { IInputTextFieldProps } from './InputTextField';

/**
 * Props for {@link DebouncedTextField}.
 *
 * Inherits all configuration props from {@link IInputTextFieldProps}, but:
 * - Omits `value` and `onChange` (managed internally via local state).
 * - Requires `setState` to be a React dispatch supporting functional updates.
 *
 * @template T - The model interface (must extend IModel).
 */
export interface IDebouncedTextFieldProps<T extends IModel>
  extends Omit<IInputTextFieldProps<T>, 'setState' | 'value' | 'onChange'> {
  /**
   * State setter from the parent component.
   * Must support functional updates `(prevState => newState)` to prevent race conditions.
   */
  setState: React.Dispatch<React.SetStateAction<T>>;

  /**
   * Debounce delay in milliseconds before dispatching state update to parent.
   * @default 300
   */
  delay?: number;
}

/**
 * `DebouncedTextField` is a performance-optimized wrapper around {@link InputTextField}.
 *
 * Decouples user keystrokes from parent state updates using a local `useState` buffer
 * and debounces synchronization to the parent form state.
 *
 * ### Key Invariants:
 * 1. **Echo Suppression (`lastCommittedRef`)**: Differentiates own outgoing debounced updates
 *    from external changes (AI autofill, form reset, product switch), preventing in-flight
 *    keystrokes from being overwritten by delayed parent re-renders.
 * 2. **Flush on Blur & Unmount**: Flushes pending changes to parent state on blur (closing the
 *    debounce window before form actions) and commits pending changes on unmount.
 * 3. **Functional State Updates**: Uses `setState(prev => ({ ...prev, [inputName]: value }))`
 *    to prevent concurrent edits in neighboring fields from clobbering each other.
 *
 * @template T - The model interface.
 */
export const DebouncedTextField = <T extends IModel>({
  inputName,
  state,
  setState,
  onBlur,
  delay = 300,
  ...props
}: IDebouncedTextFieldProps<T>) => {
  const propValue = String(state?.[inputName] ?? '');
  const [localValue, setLocalValue] = useState<string>(propValue);
  const lastCommittedRef = useRef<string>(propValue);

  const setStateRef = useRef(setState);
  setStateRef.current = setState;

  const inputNameRef = useRef(inputName);
  inputNameRef.current = inputName;

  const commit = useMemo(
    () =>
      debounce((value: string) => {
        lastCommittedRef.current = value;
        // Functional update prevents overwriting concurrent edits in neighboring fields
        setStateRef.current(prev => ({ ...prev, [inputNameRef.current]: value } as T));
      }, delay),
    [delay]
  );

  // Sync with parent state changes only when value changes externally (not from own echo)
  useEffect(() => {
    if (propValue !== lastCommittedRef.current) {
      lastCommittedRef.current = propValue;
      setLocalValue(propValue);
    }
  }, [propValue]);

  // Commit pending changes on unmount or when debounce delay changes
  useEffect(() => {
    return () => {
      commit.flush();
    };
  }, [commit]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextVal = event.target.value;
    setLocalValue(nextVal);
    commit(nextVal);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    commit.flush();
    onBlur?.(event);
  };

  return (
    <InputTextField
      {...props}
      inputName={inputName}
      state={state}
      setState={setState}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default DebouncedTextField;
